/**
 * Products tab (Phase 65 / BLU-594 PR2; server-side filters + bulk selection).
 *
 * Renders every documented state: initial skeleton, page-level error + retry,
 * first-run empty, filtered/status/sync empty, and the expandable product list
 * with cursor pagination. Search + status + sync filtering is SERVER-side
 * (fill-to-page scan), so this component never re-filters the list it is given.
 *
 * PAGINATION RENDERS IN EVERY STATE. The filters are applied by a bounded
 * catalogue scan, so an empty page is often just an empty *window* — the
 * operator has to be able to page forward from it. Early-returning the empty
 * branch above the Prev/Next controls (the old shape) made that a dead end.
 *
 * Selection is CROSS-PAGE and owned by the page (a Map keyed by product id);
 * the header checkbox here is page-scoped, and the selection count + bulk
 * actions live in the page-level FloatingSelectionBar (DESIGN §3.5 — no body
 * duplicates of an action the floating bar owns).
 *
 * `papMode` renders the PAP catalogue instead. Eligible rows support page and
 * cross-filter selection for the guarded Add-to-ERP runner; it narrows by
 * search plus the BLU-794 import
 * filter — has the PAP sweep already imported this product's SKUs? — which is
 * what turns it from a catalogue listing into a work queue like the POD side.
 * It keeps its own empty-state cascade (search / scan window / tail / empty
 * catalogue / everything imported) rather than the POD one, which keys off a
 * POD-only coverage total.
 */
import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, PackageSearch, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ProductSyncRow, type ProductSyncRowProps } from './ProductSyncRow';
import { PageLoader } from '@/components/loading/PageLoader';
import type { CodeTable, PreviewProduct } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';
import type { StagedDesignsApi } from '../hooks/useStagedDesigns';
import { isPapAddEligible } from '../lib/pap-add-eligibility';
import { pageSelectAllState } from '../lib/selection';
import type { ArtworkUploadTarget } from '../hooks/useArtworkUploadLocation';

interface ProductsTabProps {
  products: PreviewProduct[];
  hasCustomPrintSkuMapping: boolean;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  query: string;
  searchActive: boolean;
  statusFilterActive: boolean;
  /** Sync filter is narrowing the list (i.e. not 'all'). The page DEFAULTS to 'unsynced', so this is usually true. */
  syncFilterActive: boolean;
  /** Sync filter is specifically 'unsynced' — enables the terminal "nothing left to sync" state. */
  syncFilterIsUnsynced: boolean;
  /** Shared-SKU mapping filter is narrowing the list (BLU-711 Stage 2). */
  mappingFilterActive: boolean;
  /** BLU-794: the PAP import filter is narrowing the list (PAP mode only, and only while the lookup is healthy). */
  importFilterActive: boolean;
  /** Import filter is specifically 'notImported' — enables the terminal "everything is imported" state. */
  importFilterIsNotImported: boolean;
  /** BLU-711 Stage 2 dormancy gate — false hides every mapping surface on the rows. */
  sharedSkuUiEnabled: boolean;
  /** Link-state load degraded this request — per-row mapping chips are suppressed. */
  mappingStatusUnavailable: boolean;
  /** Products evaluated by the fill-scan for this page — the "no matches in this window" state quotes it. */
  scannedProducts: number;
  /** Projection lookup failed — per-row Synced chips are suppressed. */
  syncStatusUnavailable: boolean;
  /** BLU-794: PAP imported-sku lookup failed — the import filter was ignored server-side and every "Imported M/N" chip reads 0. */
  importStatusUnavailable: boolean;
  /**
   * BLU-794: the PAP view is still walking the catalogue. Rows render
   * progressively, so "0 rows" is NOT yet an answer — every empty state is
   * suppressed until the walk finishes.
   */
  isStreaming: boolean;
  /** The walk finished and drained the catalogue: the list is the COMPLETE filtered set. */
  streamComplete: boolean;
  /**
   * BLU-790: the fill-scan hit its request/time budget before draining the
   * window, so `scannedProducts === 0` means "we ran out of budget", NOT "the
   * store is empty". Load-bearing for `storeIsEmpty` below.
   */
  scanCapped: boolean;
  onClearFilters: () => void;
  /** PAP catalogue view: stock-import rows and PAP-specific empty states. */
  papMode?: boolean;
  papExtraFiltersActive?: boolean;
  papClaimsComplete?: boolean;
  papClaimsError?: string | null;
  papPendingIds?: ReadonlySet<string>;
  onRequestPapAdd?: (product: PreviewProduct) => void;
  onLoadPapResolution?: (productId: string) => Promise<import('../lib/types').PapSkuResolution>;
  onUpdatePapSkus?: ProductSyncRowProps['onUpdatePapSkus'];

  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;

  page: number;
  /** BLU-1528 R3: the exact count of products matching the filters, only when the catalogue copy served the page. */
  totalCount?: number;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  isFetching: boolean;

  buffer: ProductSyncSettingsBuffer;
  staging: StagedDesignsApi;
  onMapIt: (table: CodeTable, name?: string) => void;
  admin: boolean;
  /** Resolved colour-mode upload target + the candidates its picker offers. */
  artworkTarget: ArtworkUploadTarget;
}

function EmptyBlock({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border py-14 text-center">
      <div className="space-y-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">{body}</p>
      </div>
      {action}
    </div>
  );
}

export function ProductsTab(props: ProductsTabProps) {
  const { t } = useTranslation('productSync');
  const {
    products, hasCustomPrintSkuMapping, isLoading, isError, errorMessage, onRetry,
    query, searchActive, statusFilterActive, syncFilterActive, syncFilterIsUnsynced,
    mappingFilterActive, sharedSkuUiEnabled, mappingStatusUnavailable,
    importFilterActive, importFilterIsNotImported, importStatusUnavailable,
    isStreaming, streamComplete,
    scannedProducts, syncStatusUnavailable, scanCapped, onClearFilters, papMode = false,
    papExtraFiltersActive = false,
    papClaimsComplete = false, papClaimsError = null, papPendingIds = new Set<string>(), onRequestPapAdd, onLoadPapResolution, onUpdatePapSkus,
    expandedIds, onToggleExpand, selectedIds, onToggleSelect, onToggleSelectAll,
    page, totalCount, canPrev, canNext, onPrev, onNext, isFetching,
    buffer, staging, onMapIt, admin, artworkTarget,
  } = props;
  const papListRef = useRef<HTMLDivElement>(null);
  const virtualizePap = papMode && products.length > 200;
  const papVirtualizer = useVirtualizer({
    count: virtualizePap ? products.length : 0,
    getScrollElement: () => papListRef.current,
    estimateSize: () => 66,
    overscan: 12,
    getItemKey: (index) => products[index]?.id ?? index,
  });

  if (isLoading) return <PageLoader pool="products" />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 py-14 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-semibold">{t('states.errorTitle')}</p>
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{errorMessage ?? t('states.errorBody')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>{t('states.retry')}</Button>
      </div>
    );
  }

  const filtersActive = searchActive || statusFilterActive || syncFilterActive || mappingFilterActive;
  const showAllButton = (
    <Button variant="outline" size="sm" onClick={onClearFilters}>{t('states.clearFilters')}</Button>
  );

  /**
   * BLU-790. Was a prop derived from the store-wide coverage scan
   * (`coverage.totals.products === 0`), which defaulted to TRUE whenever
   * coverage was absent — so gating that scan showed "connect your store" to an
   * operator with 3,555 products.
   *
   * Derived here instead, from the preview's own signal. Every term is
   * load-bearing:
   *  - `scannedProducts` counts products EXAMINED, before any status/sync/
   *    mapping predicate (`catalogue-scan.ts:358`, ahead of `matches.push` at
   *    `:411`), so those filters cannot drive it to 0 and must NOT appear here.
   *    An earlier draft required "unfiltered", which — since `syncFilter`
   *    defaults to `'unsynced'` — made this permanently false and told a
   *    genuinely empty store "everything is synced" instead.
   *  - `!searchActive`: search narrows the Shopify query itself
   *    (`pod-catalogue.ts:333-336`), so it CAN legitimately yield 0.
   *  - `!canPrev`: an empty tail page past the cursor also scans 0 products.
   *  - `!scanCapped`: budget death before the first page also scans 0.
   *  - `!papMode`: PAP queries a different universe (`tag:pap`); no PAP
   *    products is not an empty store.
   *
   * BLU-794 split the derivation out as `universeIsEmpty` so the PAP branch can
   * reuse it: with an import filter on that view, "0 rows" now has two very
   * different causes — no PAP products AT ALL, versus every PAP product already
   * imported — and only `scannedProducts` (counted BEFORE any predicate) tells
   * them apart.
   */
  const universeIsEmpty = !searchActive && !canPrev && !scanCapped && scannedProducts === 0;
  const storeIsEmpty = !papMode && universeIsEmpty;

  /** Genuine first run: the store really has no POD candidates. */
  const firstRunEmpty = (
    <div className="flex flex-col items-center gap-3 rounded-lg border py-16 text-center">
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-threadheads-purple/10 text-threadheads-purple">
        <PackageSearch className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold">{t('states.emptyTitle')}</p>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">{t('states.emptyBody')}</p>
      </div>
      <Button asChild variant="outline" size="sm">
        <Link to="/settings">{t('states.emptyCta')}</Link>
      </Button>
    </div>
  );

  /**
   * This PAGE came back empty on a store that has products (stale cursor,
   * exhausted budget). Previously shared its title and body with the block
   * above, so it told an operator with a full catalogue that their store wasn't
   * connected — only the button differed.
   */
  const pageEmpty = (
    <div className="flex flex-col items-center gap-3 rounded-lg border py-16 text-center">
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-muted text-muted-foreground">
        <PackageSearch className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold">{t('states.pageEmptyTitle')}</p>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">{t('states.pageEmptyBody')}</p>
      </div>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={onRetry}>
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        {t('actions.refresh')}
      </Button>
    </div>
  );

  let body: ReactNode;
  if (products.length === 0 && isStreaming) {
    // Still walking and nothing has matched YET. Every empty state below is a
    // CLAIM about the whole catalogue ("none yet", "all imported"), and none of
    // them is knowable mid-walk — the progress line under the list is the
    // honest answer until the walk lands.
    body = null;
  } else if (products.length === 0) {
    if (papMode) {
      // Search + the BLU-794 import filter are this view's only narrowing
      // dimensions, and `storeIsEmpty` counts POD candidates — so the POD
      // first-run block and the POD "nothing left to sync" terminal both stay
      // out of this cascade. Order is load-bearing: each branch below rules
      // out a cheaper explanation for "0 rows" before the next one claims a
      // more flattering.
      if (searchActive) {
        body = (
          <EmptyBlock
            title={t('states.filteredEmptyTitle', { query })}
            body={t('papView.searchEmptyBody')}
            action={showAllButton}
          />
        );
      } else if (canNext) {
        // Bounded fill-scan: an empty page is usually an empty WINDOW.
        body = <EmptyBlock title={t('emptyStates.windowTitle')} body={t('emptyStates.windowBody', { count: scannedProducts })} />;
      } else if (canPrev) {
        // Empty tail window: there ARE PAP products, just not past here — never
        // claim "none yet" to someone who paged through them.
        body = <EmptyBlock title={t('papView.endTitle')} body={t('papView.endBody')} />;
      } else if (universeIsEmpty) {
        // The `tag:pap` catalogue is genuinely empty. Checked BEFORE the
        // "all imported" terminal: a store with no PAP products at all would
        // otherwise be congratulated on importing them (BLU-794's named trap).
        body = <EmptyBlock title={t('papView.emptyTitle')} body={t('papView.emptyBody')} />;
      } else if (papExtraFiltersActive) {
        body = <EmptyBlock title={t('states.filterEmptyTitle')} body={t('states.filterEmptyBody')} action={showAllButton} />;
      } else if (importFilterIsNotImported && scannedProducts > 0) {
        // Terminal success: PAP products WERE scanned (so the catalogue isn't
        // empty) and none of them is still waiting on the sweep. The
        // `scannedProducts > 0` term also excludes a budget-death page that
        // `universeIsEmpty` lets through on `scanCapped`.
        body = (
          <EmptyBlock
            title={t('papView.allImportedTitle')}
            body={t('papView.allImportedBody')}
            action={showAllButton}
          />
        );
      } else if (importFilterActive) {
        body = (
          <EmptyBlock title={t('states.filterEmptyTitle')} body={t('states.filterEmptyBody')} action={showAllButton} />
        );
      } else {
        body = <EmptyBlock title={t('papView.emptyTitle')} body={t('papView.emptyBody')} />;
      }
    } else if (storeIsEmpty && !searchActive) {
      // The store genuinely has no POD candidates — never dress that up as
      // "everything is synced".
      body = firstRunEmpty;
    } else if (filtersActive && canNext) {
      // Bounded fill-scan: an empty page is usually an empty WINDOW.
      body = <EmptyBlock title={t('emptyStates.windowTitle')} body={t('emptyStates.windowBody', { count: scannedProducts })} />;
    } else if (filtersActive && syncFilterIsUnsynced && !searchActive && !statusFilterActive && !mappingFilterActive) {
      // Terminal: the work queue is drained.
      body = (
        <EmptyBlock
          title={t('emptyStates.nothingToSyncTitle')}
          body={t('emptyStates.nothingToSyncBody')}
          action={showAllButton}
        />
      );
    } else if (filtersActive) {
      body = (
        <EmptyBlock
          title={searchActive ? t('states.filteredEmptyTitle', { query }) : t('states.filterEmptyTitle')}
          body={searchActive ? t('states.filteredEmptyBody') : t('states.filterEmptyBody')}
          action={showAllButton}
        />
      );
    } else {
      // Unfiltered and empty, but `storeIsEmpty` was false — so the store has
      // products and this window doesn't. Refresh, never the first-run CTA.
      body = pageEmpty;
    }
  } else {
    const selectableProducts = papMode
      ? products.filter((product) => isPapAddEligible(product, {
          importStatusUnavailable,
          clashStatusUnavailable: !papClaimsComplete || Boolean(papClaimsError),
        }))
      : products;
    // The page's toggle reads the same state, so the box's look and its click agree.
    const pageSelectState = pageSelectAllState(selectableProducts.map((product) => product.id), (id) => selectedIds.has(id));
    const renderProduct = (product: PreviewProduct) => (
      <ProductSyncRow
        key={product.id}
        product={product}
        isExpanded={expandedIds.has(product.id)}
        onToggleExpand={onToggleExpand}
        isSelected={selectedIds.has(product.id)}
        onToggleSelect={onToggleSelect}
        buffer={buffer}
        staging={staging}
        hasCustomPrintSkuMapping={hasCustomPrintSkuMapping}
        onMapIt={onMapIt}
        admin={admin}
        syncStatusUnavailable={syncStatusUnavailable}
        sharedSkuUiEnabled={sharedSkuUiEnabled && !papMode}
        mappingStatusUnavailable={mappingStatusUnavailable}
        importStatusUnavailable={importStatusUnavailable}
        papMode={papMode}
        papSkuClash={product.papSkuClash}
        papClaimsComplete={papClaimsComplete}
        papClaimsError={papClaimsError}
        papPending={papPendingIds.has(product.id)}
        onRequestPapAdd={onRequestPapAdd}
        onLoadPapResolution={onLoadPapResolution}
        onUpdatePapSkus={onUpdatePapSkus}
        artworkTarget={artworkTarget}
      />
    );
    body = (
      <div className="overflow-hidden rounded-lg border">
        {/* Page-scoped select-all. The selection COUNT and its actions live in
            the floating selection bar so there is one home for them. */}
        <div className="flex items-center gap-3 border-b bg-muted/40 px-3 py-2">
          {(!papMode || admin) && (
            <Checkbox
              checked={pageSelectState}
              onCheckedChange={onToggleSelectAll}
              disabled={papMode && selectableProducts.length === 0}
              aria-label={t('row.selectAllAria')}
            />
          )}
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            {papMode ? t('papView.listLabel') : t('tabs.products')}
          </span>
          {isFetching && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />}
        </div>

        {virtualizePap ? (
          <div ref={papListRef} className="max-h-[70vh] overflow-y-auto" data-testid="pap-virtual-list">
            <div className="relative w-full" style={{ height: papVirtualizer.getTotalSize() }}>
              {papVirtualizer.getVirtualItems().map((virtualRow) => {
                const product = products[virtualRow.index]!;
                return (
                  <div
                    key={product.id}
                    ref={papVirtualizer.measureElement}
                    data-index={virtualRow.index}
                    className="absolute left-0 top-0 w-full"
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                  >
                    {renderProduct(product)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : products.map(renderProduct)}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Explains the automatic path and the explicit action for pending work. */}
      {papMode && <p className="text-xs text-muted-foreground">{t('papView.note')}</p>}

      {body}

      {/*
        BLU-794 — PAP's footer is a PROGRESS line, not a pager: the view walks
        the whole filtered catalogue into one list, so there is nowhere to page
        to. Live region so the count is announced, but `aria-live="polite"`
        only (never assertive) — this updates once per 50-product page.
      */}
      {papMode && (isStreaming || streamComplete) && (
        <p
          role="status"
          aria-live="polite"
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          {isStreaming ? (
            <>
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              {t('papView.scanning', { scanned: scannedProducts, found: products.length })}
            </>
          ) : (
            t('papView.scanComplete', { scanned: scannedProducts, count: products.length })
          )}
        </p>
      )}

      {/* Cursor pagination — rendered for empty states too (see file header).
          BLU-1528 R3: the copy's exact total keeps the line on a single page. */}
      {(canPrev || canNext || (totalCount ?? 0) > 0) && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="tabular-nums">
            {totalCount !== undefined ? t('pagination.pageWithTotal', { page, count: totalCount }) : t('pagination.page', { page })}
          </span>
          {(canPrev || canNext) && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onPrev} disabled={!canPrev || isFetching}>
                {t('pagination.prev')}
              </Button>
              <Button variant="outline" size="sm" onClick={onNext} disabled={!canNext || isFetching}>
                {t('pagination.next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
